import { logger } from "@/utils/logger";
import { useEffect, useCallback, useRef } from 'react';
import { useStateManager } from './useStateManager';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { useOpenAIResponses } from './useOpenAIResponses';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

// Force rebuild after refactoring - removed Assistants API

interface SessionRecoveryOptions {
  onRecoveryComplete?: () => void;
  maxInactivityHours?: number;
}

export const useSessionRecovery = (options: SessionRecoveryOptions = {}) => {
  const { maxInactivityHours = 3, onRecoveryComplete } = options;
  const { state, clearState } = useStateManager();
  const { sessionData, isLoading, forceRefresh } = useDealerSession() as any;
  const { clearConversation } = useOpenAIResponses();
  const { t } = useLanguage();
  
  const lastActivityRef = useRef<number>(Date.now());
  const recoveryInProgressRef = useRef<boolean>(false);
  const visibilityCheckRef = useRef<boolean>(true);

  // Update last activity timestamp on any user interaction
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    sessionStorage.setItem('lastActivityTimestamp', Date.now().toString());
  }, []);

  // Check if session is stale based on inactivity
  const isSessionStale = useCallback(() => {
    const lastActivity = parseInt(sessionStorage.getItem('lastActivityTimestamp') || '0') || lastActivityRef.current;
    const inactiveHours = (Date.now() - lastActivity) / (1000 * 60 * 60);
    return inactiveHours >= maxInactivityHours;
  }, [maxInactivityHours]);

  // Check if OpenAI thread might be expired
  const isThreadStale = useCallback(() => {
    const threadCreated = sessionStorage.getItem('threadCreatedTimestamp');
    if (!threadCreated) return false;
    
    const threadAge = (Date.now() - parseInt(threadCreated)) / (1000 * 60 * 60);
    // OpenAI threads can become unreliable after several hours of inactivity
    return threadAge >= 4;
  }, []);

  // Perform comprehensive session recovery
  const performRecovery = useCallback(async () => {
    if (recoveryInProgressRef.current) {
      logger.log('🔄 Recovery already in progress, skipping...');
      return false;
    }

    recoveryInProgressRef.current = true;
    logger.log('🚨 Starting session recovery process...');

    try {
      // Show user-friendly notification
      toast.info(t('system.waking.up'), {
        description: t('system.refreshing.connection'),
        duration: 3000
      });

      // CRITICAL: DO NOT clear conversation for Responses API!
      // The Responses API is stateless - it uses previous_response_id for context
      // Unlike the old Assistants API, there are NO threads that expire
      // Clearing the response_id breaks conversation continuity unnecessarily
      
      // Only clear during active navigation if explicitly needed
      const navigationPending = sessionStorage.getItem('classification_navigation_pending');
      const isNavigating = navigationPending && (Date.now() - parseInt(navigationPending)) < 10000;
      
      if (isNavigating) {
        logger.log('🚫 BLOCKED: Skipping any clears during active navigation');
      } else {
        logger.log('✅ Responses API - preserving response_id for conversation continuity');
        logger.log('  (No thread expiration logic needed for Responses API)');
      }

      // 2. Force refresh dealer session if stale
      if (isSessionStale()) {
        logger.log('🔄 Refreshing stale dealer session...');
        await forceRefresh();
      }

      // 3. Clean up any stale UI state
      if (state.showNewConversationDialog) {
        clearState();
        logger.log('🧹 Cleared stale UI state');
      }

      // 4. Update activity timestamp
      updateActivity();

      logger.log('✅ Session recovery completed successfully');
      
      toast.success(t('system.connection.restored'), {
        duration: 2000
      });

      onRecoveryComplete?.();
      return true;

    } catch (error) {
      console.error('❌ Session recovery failed:', error);
      
      toast.error(t('system.recovery.failed'), {
        description: t('system.please.refresh'),
        duration: 5000
      });
      
      return false;
    } finally {
      recoveryInProgressRef.current = false;
    }
  }, [isSessionStale, isThreadStale, clearConversation, forceRefresh, clearState, state.showNewConversationDialog, updateActivity, onRecoveryComplete, t]);

  // Handle page visibility change (user returning to tab)
  const handleVisibilityChange = useCallback(async () => {
    if (document.visibilityState === 'visible' && !visibilityCheckRef.current) {
      logger.log('👁️ Tab became visible, checking session health...');
      
      // CRITICAL: DO NOT trigger recovery that clears conversation
      // The Responses API doesn't have threads that expire
      // Just update activity timestamps
      logger.log('✅ Updating activity timestamp on tab focus');
      updateActivity();
    }
    
    visibilityCheckRef.current = document.visibilityState === 'visible';
  }, [isThreadStale, performRecovery, updateActivity]);

  // Set up event listeners for activity tracking and visibility changes
  useEffect(() => {
    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize activity timestamp on mount
    updateActivity();

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateActivity, handleVisibilityChange]);

  // Response creation timestamp tracking for recovery optimization
  useEffect(() => {
    // For Responses API, we don't need thread tracking since it's stateless
    // Just ensure we have a baseline timestamp
    if (!sessionStorage.getItem('threadCreatedTimestamp')) {
      sessionStorage.setItem('threadCreatedTimestamp', Date.now().toString());
      logger.log('🧵 Marked session creation timestamp');
    }
  }, []);

  return {
    isSessionStale: isSessionStale(),
    isThreadStale: isThreadStale(),
    performRecovery,
    updateActivity,
    isRecovering: recoveryInProgressRef.current
  };
};